import * as React from 'react';
import { Link, useLocation } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { carried, replacesEntry } from './carried.ts';
import { capacity, split } from './overflow.ts';
import './rail.css';

// The navigation rail: a column of icons, each with its title under it, on
// the frame's left edge, present on every screen, so no screen needs a way
// in or out of its own. The frame declares `shell.rail` and draws nothing
// there by itself; this plugin fills it and declares `rail.entries`, which
// screens contribute to with an icon, a title, a route and an order. Entries
// marked `end` sit at the bottom. Entries sharing a `section` form a group,
// set apart from its neighbours by a divider. An entry may carry address
// parameters over (`keep`), as the dashboard screens do with `?game`.
//
// On a narrow screen (under 700px, rail.css) the rail is a row above the
// body, and a row has room for a few entries only: the ones that fit are
// shown, the active one always among them, and the rest sit behind a
// "More" button that opens a menu (overflow.ts, 2026-10-13; before, the
// row overflowed and cut the first and last entries off on a phone).
export interface RailEntryParams {
  id: string;
  title: string;
  to: string;
  /** Phosphor icon path data, viewBox 0 0 256 256. */
  icon: string;
  order?: number;
  end?: boolean;
  /** Entries with the same section sit together between dividers, e.g. 'stages'. */
  section?: string;
  /**
   * Address parameters carried over from the current address when the link
   * is followed, e.g. `['game']`: the dashboard screens keep the selected
   * game when moving between them.
   */
  keep?: string[];
}

type Extension = PiletApi['Extension'];

/** The rail is a row, not a column (rail.css's breakpoint). */
const NARROW = '(max-width: 700px)';
/** A row entry's width in rem (rail.css, `.rail-link` in the row); "More" takes one too. 3.5rem: six slots fit a 360px phone, so five entries (Home, Overview, Openings, Middlegame, Ending) and More. */
const SLOT_REM = 3.5;
/** What the row loses to its padding and dividers, in rem, before entries are counted. */
const ROW_CHROME_REM = 1.5;

// Phosphor "dots-three-outline", the More button's icon.
const DOTS = 'M140 128a12 12 0 1 1-12-12 12 12 0 0 1 12 12Zm56-12a12 12 0 1 0 12 12 12 12 0 0 0-12-12Zm-136 0a12 12 0 1 0 12 12 12 12 0 0 0-12-12Z';

const byOrder = <T extends { defaults?: { order?: number } }>(items: T[]): T[] =>
  [...items].sort((a, b) => (a.defaults?.order ?? 0) - (b.defaults?.order ?? 0));

function isActive(path: string, to: string): boolean {
  return to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`);
}

/** Whether the narrow layout applies, kept current. */
function useNarrow(): boolean {
  const [narrow, setNarrow] = React.useState(() => typeof matchMedia !== 'undefined' && matchMedia(NARROW).matches);
  React.useEffect(() => {
    const mq = matchMedia(NARROW);
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return narrow;
}

/** The element's width, kept current; 0 before it is measured. */
function useWidth(ref: React.RefObject<HTMLElement | null>, enabled: boolean): number {
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || typeof ResizeObserver === 'undefined') return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, enabled]);
  return enabled ? width : 0;
}

const Rail: React.FC<{ Extension: Extension }> = ({ Extension }) => {
  const { pathname, search } = useLocation();
  const narrow = useNarrow();
  const nav = React.useRef<HTMLElement>(null);
  const width = useWidth(nav, narrow);
  const [open, setOpen] = React.useState(false);
  // The menu closes when the address changes (a link in it was followed) and on Escape.
  React.useEffect(() => setOpen(false), [pathname]);
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <Extension
      name="rail.entries"
      order={byOrder}
      render={(children: React.ReactNode) => {
        const all = (React.Children.toArray(children) as React.ReactElement[])
          .map((el) => el.props?.params as RailEntryParams | undefined)
          .filter((p): p is RailEntryParams => !!p && typeof p.to === 'string');
        // One entry per id, the last registered winning: a screen plugin
        // re-registered by a hot reload must not appear twice.
        const byId = new Map<string, RailEntryParams>();
        for (const e of all) byId.set(e.id, e);
        const entries = [...byId.values()];
        const activeId = entries.find((e) => isActive(pathname, e.to))?.id;
        const link = (e: RailEntryParams) => {
          const active = e.id === activeId;
          return (
            <Link
              key={e.id}
              to={carried(e.to, search, e.keep)}
              replace={replacesEntry(pathname, e.to)}
              className={`rail-link${active ? ' rail-link-active' : ''}`}
              aria-current={active ? 'page' : undefined}>
              <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
                <path d={e.icon} />
              </svg>
              <span className="rail-label">{e.title}</span>
            </Link>
          );
        };
        // A divider wherever the section changes between neighbours: an
        // entry without a section is a group of its own.
        const withDividers = (list: RailEntryParams[]): React.ReactNode[] => {
          const out: React.ReactNode[] = [];
          list.forEach((e, i) => {
            const prev = list[i - 1];
            if (prev && (e.section ?? e.id) !== (prev.section ?? prev.id) && (e.section || prev.section)) {
              out.push(<hr key={`divider-${e.id}`} className="rail-divider" aria-hidden="true" />);
            }
            out.push(link(e));
          });
          return out;
        };

        if (!narrow) {
          return (
            <nav className="rail" aria-label="Main" ref={nav}>
              {withDividers(entries.filter((e) => !e.end))}
              <div className="rail-spacer" />
              {entries.filter((e) => e.end).map(link)}
            </nav>
          );
        }

        // The row: the entries in one order, top ones then end ones; the
        // ones that fit, then More for the rest.
        const ordered = [...entries.filter((e) => !e.end), ...entries.filter((e) => e.end)];
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const n = capacity(width - ROW_CHROME_REM * rem, SLOT_REM * rem, ordered.length);
        const { shown, hidden } = split(ordered, n, activeId);
        const hiddenActive = hidden.some((e) => e.id === activeId);
        return (
          <nav className="rail" aria-label="Main" ref={nav}>
            {withDividers(shown)}
            {hidden.length > 0 && (
              <>
                <button
                  type="button"
                  className={`rail-link rail-more${hiddenActive ? ' rail-link-active' : ''}`}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={() => setOpen((o) => !o)}>
                  <svg viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
                    <path d={DOTS} />
                  </svg>
                  <span className="rail-label">More</span>
                </button>
                {open && (
                  <>
                    <button type="button" className="rail-menu-backdrop" aria-label="Close the menu" onClick={() => setOpen(false)} />
                    <div className="rail-menu" role="menu" aria-label="More screens">
                      {hidden.map(link)}
                    </div>
                  </>
                )}
              </>
            )}
          </nav>
        );
      }}
    />
  );
};

export function setup(app: PiletApi) {
  app.registerExtension('shell.rail', () => <Rail Extension={app.Extension} />);
}
